import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { BarChart3, Plus, X, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BAR_COLORS = ["bg-purple-700", "bg-amber-500", "bg-slate-900", "bg-coral", "bg-purple-400"];

export default function Polls() {
  const { user } = useAuth();
  const readOnly = !!user?.read_only;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState([
    { id: crypto.randomUUID(), value: "" },
    { id: crypto.randomUUID(), value: "" },
  ]);

  const load = async () => {
    const { data } = await api.get("/polls");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const addOption = () => setOptions((o) => [...o, { id: crypto.randomUUID(), value: "" }]);
  const removeOption = (id) => setOptions((o) => o.filter((opt) => opt.id !== id));
  const setOption = (id, v) => setOptions((o) => o.map((opt) => (opt.id === id ? { ...opt, value: v } : opt)));

  const submit = async (e) => {
    e.preventDefault();
    const clean = options.map((o) => o.value.trim()).filter(Boolean);
    if (clean.length < 2) { toast.error("Need at least 2 options"); return; }
    try {
      await api.post("/polls", { question, options: clean });
      setQuestion("");
      setOptions([{ id: crypto.randomUUID(), value: "" }, { id: crypto.randomUUID(), value: "" }]);
      setOpen(false);
      toast.success("Poll created");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const vote = async (poll, idx) => {
    try {
      await api.post(`/polls/${poll.id}/vote`, { option_index: idx });
      load();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Failed to vote");
    }
  };

  const removePoll = async (poll, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this poll and all its votes?")) return;
    try {
      await api.delete(`/polls/${poll.id}`);
      toast.success("Poll deleted");
      load();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Failed to delete");
    }
  };

  const canManage = (p) => user?.role === "owner" || user?.role === "admin" || p.author_id === user?.user_id;

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-purple-700 font-bold">Decide together</p>
          <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tight">Polls</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-poll-btn" className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold btn-lift h-11 px-5">
              <Plus className="w-4 h-4 mr-1" /> New poll
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl">Create poll</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="font-semibold">Question</Label>
                <Input data-testid="poll-question-input" required value={question} onChange={(e) => setQuestion(e.target.value)} className="mt-1 rounded-xl h-11" placeholder="What should we do next?" />
              </div>
              <div>
                <Label className="font-semibold">Options</Label>
                <div className="space-y-2 mt-1">
                  {options.map((opt, i) => (
                    <div key={opt.id} className="flex items-center gap-2">
                      <Input
                        data-testid={`poll-option-input-${i}`}
                        value={opt.value}
                        onChange={(e) => setOption(opt.id, e.target.value)}
                        placeholder={`Option ${i + 1}`}
                        className="rounded-xl h-11"
                      />
                      {options.length > 2 && (
                        <button type="button" onClick={() => removeOption(opt.id)} data-testid={`poll-option-remove-${i}`} className="text-slate-400 hover:text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <Button type="button" variant="ghost" onClick={addOption} data-testid="poll-add-option-btn" className="mt-2 rounded-xl text-purple-700 hover:text-purple-800 font-semibold">
                  + Add option
                </Button>
              </div>
              <Button type="submit" data-testid="poll-submit-btn" className="w-full rounded-2xl bg-purple-700 hover:bg-purple-800 h-11 font-bold">Publish poll</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
          <BarChart3 className="w-12 h-12 text-purple-300 mx-auto" />
          <p className="mt-4 text-lg font-bold text-slate-700">No polls yet</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {items.map((p) => (
            <article key={p.id} data-testid={`poll-card-${p.id}`} className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-xl font-black font-display text-slate-900 flex-1">{p.question}</h3>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                    {p.total_votes} {p.total_votes === 1 ? "vote" : "votes"}
                  </span>
                  {canManage(p) && (
                    <button onClick={(e) => removePoll(p, e)} data-testid={`delete-poll-${p.id}`} className="text-slate-400 hover:text-red-500 transition-colors" title="Delete poll">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1">by {p.author_name}</p>
              <div className="mt-5 space-y-3">
                {p.options.map((opt, i) => {
                  const pct = p.total_votes > 0 ? Math.round((p.counts[i] / p.total_votes) * 100) : 0;
                  const isMine = p.user_vote === i;
                  const color = BAR_COLORS[i % BAR_COLORS.length];
                  return (
                    <button
                      key={`${p.id}-opt-${i}`}
                      type="button"
                      onClick={() => vote(p, i)}
                      disabled={readOnly}
                      data-testid={`poll-option-${p.id}-${i}`}
                      className={`relative w-full overflow-hidden rounded-2xl border-2 disabled:cursor-not-allowed ${
                        isMine ? "border-purple-700" : "border-slate-100"
                      } hover:border-purple-300 transition-colors group`}
                    >
                      <div
                        className={`absolute inset-y-0 left-0 ${color === "bg-coral" ? "" : color} transition-all duration-700`}
                        style={{ width: `${pct}%`, opacity: 0.18, ...(color === "bg-coral" ? { background: "#FF5757" } : {}) }}
                      />
                      <div className="relative flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-2">
                          {isMine && <Check className="w-4 h-4 text-purple-700" />}
                          <span className="font-semibold text-slate-800">{opt}</span>
                        </div>
                        <span className="text-sm font-bold text-slate-700">{pct}%</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
